import type { ModuleKey } from '@ruby/shared';
import {
  BedDouble,
  Boxes,
  Calculator,
  ClipboardList,
  Factory,
  Hammer,
  LifeBuoy,
  ReceiptText,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type Screen = LazyExoticComponent<ComponentType>;

export interface Kpi {
  label: string;
  hint: string;
}

/**
 * Front-end module registry. Each business module plugs in its look, its
 * dashboard KPIs and its screens here; permissions and menus come from the
 * shared registry. Screens are lazy-loaded so a module's code downloads only
 * when the user opens it.
 */
export interface ModuleUi {
  icon: LucideIcon;
  /** Tailwind classes for the module's icon chip (background + icon colour). */
  accent: string;
  kpis: Kpi[];
  screens?: Record<string, { list: Screen; detail?: Screen }>;
}

const admin = {
  users: { list: lazy(() => import('@/features/admin/users-list')), detail: lazy(() => import('@/features/admin/user-form')) },
  roles: { list: lazy(() => import('@/features/admin/roles-list')), detail: lazy(() => import('@/features/admin/role-form')) },
  audit_log: { list: lazy(() => import('@/features/admin/audit-log')) },
  login_history: { list: lazy(() => import('@/features/admin/login-history')) },
  company: { list: lazy(() => import('@/features/admin/company-settings')) },
  branches: { list: lazy(() => import('@/features/admin/org-list').then((m) => ({ default: m.BranchesPage }))) },
  departments: { list: lazy(() => import('@/features/admin/org-list').then((m) => ({ default: m.DepartmentsPage }))) },
  ui_kit: { list: lazy(() => import('@/features/admin/ui-kit')) },
  admin_settings: { list: lazy(() => import('@/features/admin/settings-hub')) },
  approval_policies: { list: lazy(() => import('@/features/admin/approval-policies')) },
  numbering: { list: lazy(() => import('@/features/platform/numbering')) },
  fiscal_periods: { list: lazy(() => import('@/features/platform/fiscal-periods')) },
  backdating: { list: lazy(() => import('@/features/platform/backdating')) },
  settings: { list: lazy(() => import('@/features/platform/settings')) },
  migration: { list: lazy(() => import('@/features/platform/migration')) },
  cutover: { list: lazy(() => import('@/features/platform/cutover')) },
};

const masters = (name: string) => lazy(() => import('@/features/masters/lookup-list').then((m) => ({ default: m[name as 'UomsPage'] })));
const small = (name: string) => lazy(() => import('@/features/masters/small-masters').then((m) => ({ default: m[name as 'ProductsPage'] })));

const ACCT = import.meta.glob('../features/accounting/*.tsx');
const HR = import.meta.glob('../features/hr/*.tsx');
const SALES = import.meta.glob('../features/sales/*.tsx');
const MFG = import.meta.glob('../features/mfg/*.tsx');
const mfg = (file: string, name: string) => lazy(() => (MFG[`../features/mfg/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const sal = (file: string, name: string) => lazy(() => (SALES[`../features/sales/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const hr = (file: string, name: string) => lazy(() => (HR[`../features/hr/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const INS = import.meta.glob('../features/installation/*.tsx');
const SVC = import.meta.glob('../features/service/*.tsx');
const ins = (file: string, name: string) => lazy(() => (INS[`../features/installation/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const svc = (file: string, name: string) => lazy(() => (SVC[`../features/service/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const CMD = import.meta.glob('../features/command/*.tsx');
const cmd = (name: string) => lazy(() => (CMD['../features/command/command.tsx']!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const PRE = import.meta.glob('../features/presales/*.tsx');
const pre = (file: string, name: string) => lazy(() => (PRE[`../features/presales/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const COST = import.meta.glob('../features/costing/*.tsx');
const cost = (file: string, name: string) => lazy(() => (COST[`../features/costing/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const MTR = import.meta.glob('../features/mattress/*.tsx');
const mtr = (file: string, name: string) => lazy(() => (MTR[`../features/mattress/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const INV = import.meta.glob('../features/inventory/*.tsx');
const inv = (file: string, name: string) => lazy(() => (INV[`../features/inventory/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));
const acct = (file: string, name: string) => lazy(() => (ACCT[`../features/accounting/${file}.tsx`]!() as Promise<Record<string, ComponentType>>).then((m) => ({ default: m[name]! })));

export const MODULE_UI: Record<ModuleKey, ModuleUi> = {
  sales: {
    icon: ClipboardList,
    accent: 'bg-[#e6f0ff] text-[#1d5bd8] dark:bg-[#1d5bd8]/20 dark:text-[#83a5ea]',
    kpis: [
      { label: 'Open sales orders', hint: 'Orders not yet fully delivered' },
      { label: 'Orders this month', hint: 'Booked in the current month' },
      { label: 'Order value (AED)', hint: 'Month to date' },
      { label: 'Pending approvals', hint: 'Awaiting your approval' },
    ],
    screens: {
      enquiries: { list: pre('enquiries', 'EnquiriesPage'), detail: pre('enquiries', 'EnquiryPage') },
      design_board: { list: pre('design-board', 'DesignBoardPage') },
      release_gate: { list: pre('release', 'ReleaseGatePage') },
      customers: {
        list: lazy(() => import('@/features/masters/partners-list').then((m) => ({ default: m.CustomersListPage }))),
        detail: lazy(() => import('@/features/masters/partner-form').then((m) => ({ default: m.CustomerFormPage }))),
      },
      salespersons: { list: small('SalespersonsPage') },
      quotations: { list: sal('lists', 'QuotationsPage'), detail: sal('doc-form', 'QuotationPage') },
      sales_orders: { list: sal('lists', 'SalesOrdersPage'), detail: sal('doc-form', 'SalesOrderPage') },
      catalogue: { list: sal('catalogue', 'CataloguePage'), detail: sal('catalogue', 'CatalogueItemPage') },
      customer_360: { list: sal('reports', 'Customer360Page') },
      sales_reports: { list: sal('reports', 'SalesReportsHub') },
    },
  },
  inventory: {
    icon: Boxes,
    accent: 'bg-[#fff0e3] text-[#c2410c] dark:bg-[#c2410c]/20 dark:text-[#dd9679]',
    kpis: [
      { label: 'Items below reorder level', hint: 'Need purchasing' },
      { label: 'Open purchase orders', hint: 'Not yet fully received' },
      { label: 'Pending requisitions', hint: 'Awaiting issue' },
      { label: 'Stock value (AED)', hint: 'All warehouses' },
    ],
    screens: {
      approvals: { list: inv('approvals', 'InvApprovalsPage') },
      order_material: { list: inv('order-material', 'OrderMaterialPage') },
      special_requests: { list: inv('store-docs', 'SpecialRequestsPage'), detail: inv('store-docs', 'SpecialRequestPage') },
      out_gate_passes: { list: inv('store-docs', 'OutGatePassesPage'), detail: inv('store-docs', 'OutGatePassPage') },
      shelves: { list: inv('store-docs', 'ShelvesPage') },
      planning_check: { list: inv('planning-check', 'PlanningCheckPage') },
      production_returns: { list: inv('lists', 'ProductionReturnsList'), detail: inv('doc-form', 'ProductionReturnPage') },
      reorder: { list: inv('reorder', 'ReorderPage') },
      held_stock: { list: inv('held-stock', 'HeldStockPage') },
      purchase_requisitions: { list: inv('lists', 'RequisitionsList'), detail: inv('doc-form', 'RequisitionPage') },
      purchase_orders: { list: inv('lists', 'PurchaseOrdersList'), detail: inv('doc-form', 'PurchaseOrderPage') },
      gate_passes: { list: inv('gate-passes', 'GatePassesPage'), detail: inv('gate-passes', 'GatePassPage') },
      stock_counts: { list: inv('stock-counts', 'StockCountsPage'), detail: inv('stock-counts', 'StockCountPage') },
      goods_receipts: { list: inv('lists', 'GoodsReceiptsList'), detail: inv('doc-form', 'GoodsReceiptPage') },
      purchase_bills: { list: inv('lists', 'BillsList'), detail: inv('doc-form', 'BillPage') },
      purchase_returns: { list: inv('lists', 'PurchaseReturnsList'), detail: inv('doc-form', 'PurchaseReturnPage') },
      stock_issues: { list: inv('lists', 'StockIssuesList'), detail: inv('doc-form', 'StockIssuePage') },
      stock_transfers: { list: inv('lists', 'StockTransfersList'), detail: inv('doc-form', 'StockTransferPage') },
      stock_adjustments: { list: inv('lists', 'StockAdjustmentsList'), detail: inv('doc-form', 'StockAdjustmentPage') },
      stock_valuation: { list: inv('stock-reports', 'ClosingStockPage') },
      inventory_reports: { list: lazy(() => import('@/features/inventory/stock-reports')) },
      store_reports: { list: inv('store-reports', 'StoreReportsHub') },
      inventory_settings: { list: inv('settings-hub', 'InventorySettingsPage') },
      purchasing_rules: { list: inv('settings-hub', 'PurchasingRulesPage') },
      purchasing_cutover: { list: inv('settings-hub', 'PurchasingCutoverPage') },
      items: { list: lazy(() => import('@/features/masters/items')), detail: lazy(() => import('@/features/masters/items').then((m) => ({ default: m.ItemFormPage }))) },
      raw_materials: { list: lazy(() => import('@/features/masters/items').then((m) => ({ default: m.RawMaterialsPage }))), detail: lazy(() => import('@/features/masters/items').then((m) => ({ default: m.ItemFormPage }))) },
      finished_goods: { list: lazy(() => import('@/features/masters/items').then((m) => ({ default: m.FinishedGoodsPage }))), detail: lazy(() => import('@/features/masters/items').then((m) => ({ default: m.ItemFormPage }))) },
      departments: admin.departments,
      products: { list: inv('products', 'ProductsPage'), detail: inv('products', 'ProductPage') },
      product_attributes: { list: inv('products', 'ProductAttributesPage') },
      item_categories: { list: masters('CategoriesPage') },
      uoms: { list: masters('UomsPage') },
      warehouses: { list: masters('WarehousesPage') },
      production_sections: { list: masters('ProductionSectionsPage') },
      suppliers: {
        list: lazy(() => import('@/features/masters/partners-list').then((m) => ({ default: m.SuppliersListPage }))),
        detail: lazy(() => import('@/features/masters/partner-form').then((m) => ({ default: m.SupplierFormPage }))),
      },
    },
  },
  mattress: {
    icon: BedDouble,
    accent: 'bg-[#e8e9fd] text-[#4f46e5] dark:bg-[#4f46e5]/20 dark:text-[#a5a1f7]',
    kpis: [],
    screens: {
      command_centre: { list: cmd('MattressCommandPage') },
      mattress_boms: { list: mtr('boms', 'BomsListPage'), detail: mtr('boms', 'BomPage') },
      mattress_demand: { list: mtr('orders', 'DemandPage') },
      production_plan: { list: mtr('orders', 'PlanPage') },
      production_orders: { list: mtr('orders', 'OrdersListPage'), detail: mtr('orders', 'OrderPage') },
      production_entries: { list: mtr('entries', 'EntriesListPage'), detail: mtr('entries', 'EntryPage') },
      missing_entries: { list: mtr('entries', 'MissingEntriesPage') },
      mattress_reports: { list: mtr('report-hub', 'MattressReportHub') },
      mattress_settings: { list: mtr('settings', 'MattressSettingsPage') },
      mattress_stages: { list: mtr('settings', 'StagesPage') },
    },
  },
  manufacturing: {
    icon: Factory,
    accent: 'bg-[#dff3e8] text-[#0f5132] dark:bg-[#0f5132]/20 dark:text-[#7b9f8e]',
    kpis: [
      { label: 'Jobs in production', hint: 'Released or in progress' },
      { label: 'Due this week', hint: 'Planned completion' },
      { label: 'Drawings to review', hint: 'Waiting for production' },
      { label: 'Completed this month', hint: 'Finished goods received' },
    ],
    screens: {
      command_centre: { list: cmd('FurnitureCommandPage') },
      drawings: { list: mfg('commitments', 'DrawingsPage') },
      unplanned_orders: { list: mfg('planning', 'UnplannedOrdersPage') },
      material_plans: { list: mfg('plan-materials', 'MaterialPlansListPage'), detail: mfg('plan-materials', 'MaterialPlanPage') },
      planning_studio: { list: mfg('plan-studio', 'PlanningStudioListPage'), detail: mfg('plan-studio', 'PlanningStudioPage') },
      work_orders: { list: mfg('planning', 'JobsListPage'), detail: mfg('planning', 'JobPage') },
      job_board: { list: mfg('board', 'JobBoardPage') },
      calendar_board: { list: mfg('studio', 'CalendarBoardPage') },
      capacity: { list: mfg('studio', 'CapacityPage') },
      allocation_board: { list: mfg('planning-timeline', 'PlanningTimelinePage') },
      material_requisitions: { list: mfg('floor', 'MrnListPage'), detail: mfg('floor', 'MrnPage') },
      labour: { list: mfg('floor', 'ShopFloorPage') },
      job_cards: { list: mfg('job-cards', 'JobCardsPage') },
      quality_checks: { list: mfg('board', 'QcQueuePage') },
      fg_receipts: { list: mfg('floor', 'FgReceiptsPage') },
      production_setup: { list: mfg('setup', 'ProductionSetupPage') },
      skills: { list: mfg('studio', 'SkillsPage') },
      standard_times: { list: mfg('studio', 'StandardTimesPage') },
      production_reports: { list: mfg('report-hub', 'FurnitureReportHub') },
    },
  },
  installation: {
    icon: Hammer,
    accent: 'bg-[#fdf0e3] text-[#c2410c] dark:bg-[#c2410c]/20 dark:text-[#f0a774]',
    kpis: [
      { label: 'To schedule', hint: 'Delivered, no visit booked' },
      { label: 'Overdue', hint: 'Past the installation target' },
      { label: 'Open snags', hint: 'Still to fix at the site' },
      { label: 'Handed over this month', hint: 'Signed by the customer' },
    ],
    screens: {
      command_centre: { list: cmd('InstallationCommandPage') },
      installation_orders: { list: ins('orders', 'InstallationOrdersPage'), detail: ins('orders', 'InstallationOrderPage') },
      team_board: { list: ins('board', 'TeamBoardPage') },
      snags: { list: ins('reports', 'SnagsPage') },
      my_installations: { list: ins('board', 'MyInstallationsPage') },
      installation_reports: { list: ins('reports', 'InstallationReportsHub') },
    },
  },
  service: {
    icon: LifeBuoy,
    accent: 'bg-[#fdecef] text-[#be123c] dark:bg-[#be123c]/20 dark:text-[#f08aa0]',
    kpis: [
      { label: 'New', hint: 'Waiting for assessment' },
      { label: 'In repair', hint: 'Repair job, visit or replacement under way' },
      { label: 'Resolved this month', hint: 'Fixed and confirmed' },
      { label: 'After-sales cost this month (AED)', hint: 'Repairs, visits and replacements at cost' },
    ],
    screens: {
      service_tickets: { list: svc('tickets', 'ServiceTicketsPage'), detail: svc('tickets', 'ServiceTicketPage') },
      service_board: { list: svc('board', 'ServiceBoardPage') },
      service_reports: { list: svc('board', 'ServiceReportsHub') },
    },
  },
  invoicing: {
    icon: ReceiptText,
    accent: 'bg-[#e3f6fb] text-[#0e7490] dark:bg-[#0e7490]/20 dark:text-[#7ab3c2]',
    kpis: [
      { label: 'Ready to deliver', hint: 'Finished goods awaiting delivery' },
      { label: 'Delivered, not invoiced', hint: 'Needs a tax invoice' },
      { label: 'Invoiced this month (AED)', hint: 'Including VAT' },
      { label: 'Draft invoices', hint: 'Not yet posted' },
    ],
    screens: {
      delivery_notes: { list: sal('lists', 'DeliveriesPage'), detail: sal('doc-form', 'DeliveryPage') },
      tax_invoices: { list: sal('lists', 'InvoicesPage'), detail: sal('doc-form', 'InvoicePage') },
      credit_notes: { list: sal('lists', 'CreditNotesPage'), detail: sal('doc-form', 'CreditNotePage') },
      invoicing_reports: { list: sal('reports', 'InvoicingReportsPage') },
    },
  },
  accounting: {
    icon: Calculator,
    accent: 'bg-[#f1e9ff] text-[#6d28d9] dark:bg-[#6d28d9]/20 dark:text-[#af89ea]',
    kpis: [
      { label: 'Receivables (AED)', hint: 'Outstanding from customers' },
      { label: 'Payables (AED)', hint: 'Due to suppliers' },
      { label: 'Unposted vouchers', hint: 'Drafts awaiting posting' },
      { label: 'Bank balance (AED)', hint: 'All bank accounts' },
    ],
    screens: {
      chart_of_accounts: { list: lazy(() => import('@/features/accounting/coa')) },
      posting_accounts: { list: acct('coa', 'PostingAccountsPage') },
      voucher_change_settings: { list: acct('change-settings', 'VoucherChangeSettingsPage') },
      all_vouchers: { list: acct('vouchers-list', 'AllVouchersPage') },
      voucher_manager: { list: acct('voucher-manager', 'VoucherManagerPage'), detail: acct('voucher-manager', 'VoucherManagerVoucher') },
      // The old stage screens open the matching Voucher Manager card; their voucher pages keep working.
      pending_vouchers: { list: acct('voucher-manager', 'PendingRedirect'), detail: acct('approvals', 'PendingVoucherPage') },
      rectification_vouchers: { list: acct('voucher-manager', 'RectificationRedirect'), detail: acct('approvals', 'RectificationVoucherPage') },
      approved_vouchers: { list: acct('voucher-manager', 'ApprovedRedirect'), detail: acct('approvals', 'ApprovedVoucherPage') },
      cancelled_vouchers: { list: acct('voucher-manager', 'CancelledRedirect'), detail: acct('approvals', 'CancelledVoucherPage') },
      period_close: { list: acct('period-close', 'PeriodClosePage') },
      overhead_rate: { list: cost('overhead', 'OverheadRatePage') },
      cost_variance: { list: cost('reports', 'CostVariancePage') },
      order_profitability: { list: cost('reports', 'OrderProfitabilityPage') },
      production_close: { list: cost('close', 'ProductionClosePage') },
      general_ledger: { list: acct('reports', 'GeneralLedgerPage') },
      accounting_settings: { list: acct('settings-hub', 'AccountingSettingsPage') },
      journal_vouchers: { list: acct('vouchers-list', 'JournalsListPage'), detail: acct('voucher-form', 'JournalPage') },
      receipts: { list: acct('vouchers-list', 'ReceiptsListPage'), detail: acct('voucher-form', 'ReceiptPage') },
      payments: { list: acct('vouchers-list', 'PaymentsListPage'), detail: acct('voucher-form', 'PaymentPage') },
      allocations: { list: acct('advances', 'AdjustAdvancesPage') },
      purchase_vouchers: { list: acct('purchase-vouchers', 'PurchaseVouchersPage') },
      salary_vouchers: { list: acct('salary-vouchers', 'SalaryVouchersPage') },
      cheques: { list: acct('treasury', 'ChequesPage') },
      recovery_cases: { list: acct('recovery', 'RecoveryListPage'), detail: acct('recovery', 'RecoveryCasePage') },
      bank_reconciliation: { list: acct('treasury', 'BankRecListPage'), detail: acct('treasury', 'BankRecPage') },
      financial_reports: { list: lazy(() => import('@/features/accounting/reports')) },
      ledger_reports: { list: acct('ledger-reports', 'LedgerReportsHub') },
      customer_outstanding: { list: acct('outstanding', 'CustomerOutstandingPage') },
      supplier_outstanding: { list: acct('outstanding', 'SupplierOutstandingPage') },
      salesperson_outstanding: { list: acct('outstanding', 'SalespersonOutstandingPage') },
      approval_report: { list: acct('approval-report', 'ApprovalReportPage') },
      fixed_assets: { list: acct('assets', 'FixedAssetsPage') },
      management_reports: { list: lazy(() => import('@/features/accounting/management')) },
      vat_return: { list: sal('reports', 'VatReturnPage') },
      legacy_reconciliation: { list: acct('reports', 'LegacyReconciliationPage') },
      cost_centres: { list: masters('CostCentresPage') },
      tax_codes: { list: masters('TaxCodesPage') },
      payment_terms: { list: masters('PaymentTermsPage') },
      bank_accounts: { list: small('BankAccountsPage') },
      cheque_books: { list: acct('cheque-books', 'ChequeBooksPage'), detail: acct('cheque-books', 'ChequeBookPage') },
    },
  },
  hr: {
    icon: Users,
    accent: 'bg-[#fde8ef] text-[#be185d] dark:bg-[#be185d]/20 dark:text-[#db80a6]',
    kpis: [
      { label: 'Active employees', hint: 'On payroll' },
      { label: 'Present today', hint: 'Attendance' },
      { label: 'On leave', hint: 'Today' },
      { label: 'Next payroll', hint: 'Upcoming run' },
    ],
    screens: {
      employees: { list: hr('employees', 'default'), detail: hr('employees', 'EmployeeFormPage') },
      designations: { list: masters('DesignationsPage') },
      salaries: { list: hr('salaries', 'SalariesPage') },
      attendance: { list: hr('attendance', 'default') },
      leave_requests: { list: hr('docs', 'LeaveListPage'), detail: hr('docs', 'LeavePage') },
      leave_balances: { list: hr('reports', 'LeaveBalancesAt') },
      work_schedules: { list: hr('settings-hub', 'PoliciesAt'), detail: hr('shifts', 'ShiftPage') },
      shift_periods: { list: hr('settings-hub', 'PoliciesAt') },
      attendance_policies: { list: hr('settings-hub', 'PoliciesAt'), detail: hr('policies', 'AttendancePolicyPage') },
      attendance_devices: { list: hr('devices', 'DevicesPage') },
      policies: { list: hr('settings-hub', 'PoliciesAt'), detail: hr('settings-hub', 'HandbookFullPage') },
      holidays: { list: hr('settings-hub', 'PoliciesAt'), detail: hr('settings-hub', 'HolidaysFullPage') },
      leave_types: { list: hr('settings-hub', 'PoliciesAt'), detail: hr('settings-hub', 'LeaveTypesDetail') },
      payroll_runs: { list: hr('payroll', 'PayrollRunsPage'), detail: hr('payroll', 'PayrollRunPage') },
      loans: { list: hr('docs', 'LoansListPage'), detail: hr('docs', 'LoanPage') },
      gratuity: { list: hr('gratuity', 'GratuityPage') },
      air_tickets: { list: hr('air-tickets', 'AirTicketsPage') },
      final_settlements: { list: hr('settlements', 'SettlementsListPage'), detail: hr('settlements', 'SettlementPage') },
      payroll_adjustments: { list: hr('setup', 'AdjustmentsPage') },
      hr_settings: { list: hr('settings-hub', 'HrSettingsPage') },
      hr_reports: { list: hr('reports', 'default') },
      allowances: { list: hr('reports', 'AllowancesAt') },
      activity_log: { list: hr('history', 'ActivityLogPage') },
    },
  },
  admin: {
    icon: ShieldCheck,
    accent: 'bg-[#eef1f4] text-[#334155] dark:bg-[#334155]/20 dark:text-[#8f96a2]',
    kpis: [],
    screens: admin,
  },
};
